'use client';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import EditModal from './edit-modal';
import { Button } from '@/components/ui/buttons';
import { signOut, useSession } from 'next-auth/react';
import { FaSignOutAlt, FaTrash } from 'react-icons/fa';
import useCart from '@/store/cart.store';
import useCheckout from '@/store/checkout.store';
import DeleteModal from '@/components/ui/modals/delete-modal';
import { deleteAccount } from '@/services/user/account';
import { toast } from 'sonner';
import { deleteCookie, setCookie } from 'cookies-next';

function Index() {
  const t = useTranslations('user');
  const [isOpen, setIsOpen] = useState(null);
  const { data: session, status } = useSession();
  const { resetCart } = useCart();
  const { clearCheckout } = useCheckout();

  const { firstname, lastname, email, mobilenumber } = session?.user || {};

  const [isDeleting, setIsDeleting] = useState(false);
  const [isOpenDelete, setIsOpenDelete] = useState(false);

  const clearCookiesAndSignOut = async () => {
    deleteCookie('quote');
    deleteCookie('mage');
    setCookie('user_logout_event', JSON.stringify(session?.user));
    signOut({ callbackUrl: '/' });
  };
  const handleDelete = async () => {
    setIsDeleting(true);
    deleteAccount(session?.mage, session?.user?.id).then((res) => {
      if (res?.data) {
        setIsDeleting(false);
        setIsOpenDelete(false);
        clearCookiesAndSignOut();
      } else {
        toast.error(res?.error?.error?.message);
        setIsDeleting(false);
      }
    });
  };

  return (
    <section className='flex flex-col gap-[25px] max-lg:gap-5 max-md:gap-4.5 max-sm:gap-[13px]'>
      <div className='flex items-center justify-between'>
        <h2 className='text-xl leading-[100%] font-bold max-lg:hidden'>
          {t('login_and_security_key')}
        </h2>
        <Button
          onClick={async () => {
            await resetCart();
            await clearCheckout();
            clearCookiesAndSignOut();
          }}
          className='lg:!text-primary max-lg:bg-primary bg-transparent text-base font-semibold hover:bg-transparent hover:underline max-lg:h-11 max-lg:w-full max-lg:text-[15px] max-lg:text-white max-md:h-10 max-md:text-sm max-sm:h-8 max-sm:text-xs'
        >
          <FaSignOutAlt className='lg:hidden' />
          {t('sign_out_key')}
        </Button>
      </div>
      <div className='border-border rounded-lg border max-lg:px-6 max-lg:pt-8 max-lg:pb-6.5 max-md:py-6.5 max-sm:py-[21px] max-sm:ps-6 max-sm:pe-[13px] lg:px-[35px] lg:pt-[37px] lg:pb-7.5'>
        <div className='max-lg:space-y-[40px] max-md:space-y-[32px] max-sm:space-y-[26px] lg:space-y-[55px]'>
          <div className='flex items-center justify-between'>
            <div className='flex flex-col max-lg:gap-5.5 max-md:gap-4.75 max-sm:gap-4 lg:gap-[27px]'>
              <h3 className='leading-none max-lg:text-[15px] max-md:text-sm max-sm:text-xs lg:font-bold'>
                {t('name_key')}
              </h3>
              {status === 'loading' ? (
                <div className='mt-1 h-4 w-24 animate-pulse rounded-full bg-gray-200' />
              ) : (
                <p className='leading-none max-lg:text-[15px] max-md:text-sm max-sm:text-xs max-sm:font-bold'>
                  {firstname} {lastname}
                </p>
              )}
            </div>
            <Button
              disabled={status === 'loading'}
              className='!bg-primary h-10 w-[138px] max-lg:h-8.5 max-md:h-7.75 max-md:text-xs max-sm:h-[25px] max-sm:w-[75px] max-sm:text-xs'
              onClick={() => setIsOpen('name')}
            >
              {t('edit_key')}
            </Button>
          </div>

          <div className='flex items-center justify-between'>
            <div className='flex flex-col max-lg:gap-5.5 max-md:gap-4.75 max-sm:gap-4 lg:gap-[27px]'>
              <h3 className='leading-none max-lg:text-[15px] max-md:text-sm max-sm:text-xs lg:font-bold'>
                {t('email_key')}
              </h3>
              {status === 'loading' ? (
                <div className='mt-1 h-4 w-24 animate-pulse rounded-full bg-gray-200' />
              ) : (
                <p className='leading-none max-lg:text-[15px] max-md:text-sm max-sm:text-xs max-sm:font-bold'>
                  {email}
                </p>
              )}
            </div>
            <Button
              disabled={status === 'loading'}
              className='!bg-primary h-10 w-[138px] max-lg:h-8.5 max-md:h-7.75 max-md:text-xs max-sm:h-[25px] max-sm:w-[75px] max-sm:text-xs'
              onClick={() => setIsOpen('email')}
            >
              {t('edit_key')}
            </Button>
          </div>

          <div className='flex items-center justify-between'>
            <div className='flex flex-col max-lg:gap-5.5 max-md:gap-4.75 max-sm:gap-4 lg:gap-[27px]'>
              <h3 className='leading-none max-lg:text-[15px] max-md:text-sm max-sm:text-xs lg:font-bold'>
                {t('primary_mobile_number_key')}
              </h3>
              {status === 'loading' ? (
                <div className='mt-1 h-4 w-24 animate-pulse rounded-full bg-gray-200' />
              ) : (
                mobilenumber ? <p className='leading-none max-lg:text-[15px] max-md:text-sm max-sm:text-xs max-sm:font-bold'>
                  {`0${mobilenumber?.slice(-9)}`}
                </p> : null
              )}
            </div>
            <Button
              disabled={status === 'loading'}
              className='!bg-primary h-10 w-[138px] max-lg:h-8.5 max-md:h-7.75 max-md:text-xs max-sm:h-[25px] max-sm:w-[75px] max-sm:text-xs'
              onClick={() => setIsOpen('phone_number')}
            >
              {t('edit_key')}
            </Button>
          </div>

          <div className='flex items-center justify-between'>
            <div className='flex flex-col max-lg:gap-5.5 max-md:gap-4.75 max-sm:gap-4 lg:gap-[27px]'>
              <h3 className='leading-none max-lg:text-[15px] max-md:text-sm max-sm:text-xs lg:font-bold'>
                {t('password_key')}
              </h3>
              <p className='leading-none max-lg:text-[15px] max-md:text-sm max-sm:text-xs max-sm:font-bold'>
                ************
              </p>
            </div>
            <Button
              disabled={status === 'loading'}
              className='!bg-primary h-10 w-[138px] max-lg:h-8.5 max-md:h-7.75 max-md:text-xs max-sm:h-[25px] max-sm:w-[75px] max-sm:text-xs'
              onClick={() => setIsOpen('password')}
            >
              {t('edit_key')}
            </Button>
          </div>
        </div>
      </div>
      <div className='flex items-center'>
        <Button
          onClick={() => setIsOpenDelete(true)}
          className='w-60 max-lg:h-9 max-lg:w-full max-sm:h-8 max-sm:text-xs lg:h-12'
        >
          <FaTrash className='size-4 max-lg:size-3 max-md:size-2.5 max-sm:size-2' />
          {t('delete_account_key')}
        </Button>
      </div>
      {isOpen && (
        <EditModal isOpen={isOpen} setIsOpen={setIsOpen} session={session} />
      )}
      {isOpenDelete && (
        <DeleteModal
          title={t('delete_account_key')}
          message={t('delete_account_msg_key')}
          onDelete={handleDelete}
          isDeleting={isDeleting}
          isOpen={isOpenDelete}
          onClose={() => setIsOpenDelete(false)}
        />
      )}
    </section>
  );
}

export default Index;
